import React, { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import { useI18n } from "@/lib/i18n";
import { http } from "@/lib/api";

export default function CrawlPolicy() {
  const { locale } = useI18n();
  // CR-01: hydrate the bot-identity strings from the backend so we cannot
  // drift from what our crawler actually sends. Falls back to a placeholder
  // if the endpoint is unreachable.
  const [policy, setPolicy] = useState({ bot_token: "BrandPassportBot", user_agent: "BrandPassportBot/1.0 (+https://brandpassport.ai/crawl-policy)" });
  useEffect(() => {
    http.get("/crawl-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <Seo title="Crawl policy" description="How BrandPassport's crawler operates and how to opt out." canonicalPath="/crawl-policy" locale={locale} />
      <div className="bp-container py-16 max-w-3xl">
        <h1 className="font-serif text-5xl mb-6">Crawl policy</h1>
        <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>
          <p>BrandPassport operates an automated crawler that retrieves publicly available web pages to build brand transparency profiles. This page explains how the crawler behaves and how site operators can opt out.</p>

          <h2 className="font-serif text-2xl mt-6">User-agent</h2>
          <p className="font-mono text-sm bg-white border p-3 inline-block" style={{ borderColor: "var(--bp-border)" }} data-testid="crawl-ua">
            {policy.user_agent}
          </p>

          <h2 className="font-serif text-2xl mt-6">Signed requests (Web Bot Auth)</h2>
          <p>Every request our crawler makes is signed with an Ed25519 HTTP Message Signature (RFC 9421). Site operators — including Cloudflare Bot Management customers — can verify that a request truly comes from us instead of relying on the user-agent header alone.</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Signature covers <code>@method</code>, <code>@target-uri</code>, and <code>signature-agent</code>.</li>
            <li>Headers sent: <code>Signature</code>, <code>Signature-Input</code>, <code>Signature-Agent</code>.</li>
            <li>Public key directory: <a className="bp-link font-mono text-sm" href="/.well-known/http-message-signatures-directory" data-testid="crawl-directory-link">/.well-known/http-message-signatures-directory</a> (JWK Set, Ed25519).</li>
          </ul>

          <h2 className="font-serif text-2xl mt-6">Egress IPs</h2>
          <p>When we run behind a fixed egress proxy, the outbound IPs are published in the directory document above under <code>egress_ips</code>. Site operators may allowlist by IP + verified signature for the highest trust level.</p>

          <h2 className="font-serif text-2xl mt-6">Purpose</h2>
          <p>We retrieve only publicly-served web pages linked from a brand&apos;s official domain(s) to identify disclosures relevant to the seven transparency categories BrandPassport indexes (product identity, materials, origin, repair &amp; care, end of life, environmental footprint, certifications). We do not attempt to bypass authentication, robots.txt, or paywalls.</p>

          <h2 className="font-serif text-2xl mt-6">Frequency &amp; rate limits</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Maximum one request every 2 seconds per domain.</li>
            <li>Maximum crawl depth of 3 hops from the seed URL.</li>
            <li>Maximum 15 candidate URLs indexed per brand.</li>
            <li>We refresh profiles at most every 90 days on a rolling schedule.</li>
          </ul>

          <h2 className="font-serif text-2xl mt-6">robots.txt compliance</h2>
          <p>We honour <code>robots.txt</code> directives. If your site disallows <code data-testid="crawl-bot-token-1">{policy.bot_token}</code> or all user-agents on a given path, we will not fetch content from that path.</p>

          <h2 className="font-serif text-2xl mt-6">Opt out</h2>
          <p>To exclude your domain entirely:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Add a rule to <code>robots.txt</code>: <code data-testid="crawl-robots-directive">User-agent: {policy.bot_token} / Disallow: /</code></li>
            <li>Or email <a className="bp-link" href="mailto:crawl@brandpassport.ai">crawl@brandpassport.ai</a> from the domain owner&apos;s address; we honour takedown requests within 5 business days.</li>
          </ul>

          <h2 className="font-serif text-2xl mt-6">Contact</h2>
          <p><a className="bp-link" href="mailto:crawl@brandpassport.ai">crawl@brandpassport.ai</a></p>
        </div>
      </div>
    </>
  );
}
